import { Fragment } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import type { AuditEvent } from '@/lib/api/audit';

import {
  formatAuditValue,
  getActionLabelKey,
  getRealmBadge,
  humanizeAction,
  type AuditRealmBadge,
} from '../utils/audit-labels';
import { AuditChangesDiff, formatTruncatedSize } from './AuditChangesDiff';

/**
 * Derived from the `audit.list` tRPC output rather than hand-written: the row
 * has 27 columns and a local copy would drift from `src/lib/schemas/auditEvents.ts`
 * silently. Requires `packages/trpc-types/dist/` to be rebuilt after the router
 * is registered — that `dist/` is committed, like the Studio bundles.
 */
export type AuditEventRow = AuditEvent;

export type AuditEventDetailSheetProps = {
  event: AuditEventRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function RealmBadge({ badge }: { badge: AuditRealmBadge }) {
  const { t } = useTranslation('settings');
  const content = <Badge variant={badge.variant}>{t(badge.labelKey)}</Badge>;

  if (!badge.tooltipKey) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{t(badge.tooltipKey)}</TooltipContent>
    </Tooltip>
  );
}

function ContextValue({ value }: { value: unknown }) {
  const { t } = useTranslation('settings');
  const formatted = formatAuditValue(value);

  if (formatted.kind === 'truncated') {
    return (
      <span className="text-muted-foreground italic">
        {t('auditTrail.diff.truncated', { size: formatTruncatedSize(formatted.bytes ?? 0) })}
      </span>
    );
  }

  if (formatted.kind === 'empty') {
    return <span className="text-muted-foreground">—</span>;
  }

  if (formatted.kind === 'json') {
    return (
      <div className="overflow-x-auto">
        <pre className="text-xs">{formatted.text}</pre>
      </div>
    );
  }

  return <span>{formatted.text}</span>;
}

export function AuditEventDetailSheet({ event, open, onOpenChange }: AuditEventDetailSheetProps) {
  const { t } = useTranslation('settings');

  if (!event) return null;

  const realmBadge = getRealmBadge(event.actorRealm);
  const actorDisplayName = event.actorLabel ?? event.actorEmail ?? event.actorId;
  const title = t(getActionLabelKey(event.action), { defaultValue: humanizeAction(event.action) });
  // `fieldsTouched` is already rendered inside AuditChangesDiff's fields-touched state.
  const remainingContext = Object.entries(event.context).filter(([key]) => key !== 'fieldsTouched');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex flex-wrap items-center gap-2 pr-8">
            <SheetTitle className="text-base">{title}</SheetTitle>
            <RealmBadge badge={realmBadge} />
          </div>
          <SheetDescription>{format(event.createdAt, 'MMM dd, yyyy HH:mm')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 px-6 py-6">
          <section className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">{t('auditTrail.columns.actor')}: </span>
              <span className="font-medium">{actorDisplayName}</span>
              {event.actorEmail && event.actorEmail !== actorDisplayName && (
                <span className="text-muted-foreground"> ({event.actorEmail})</span>
              )}
            </div>
            {event.onBehalfOfEmail && (
              <div className="text-muted-foreground">
                {t('auditTrail.detail.onBehalfOf', { email: event.onBehalfOfEmail })}
              </div>
            )}
          </section>

          <section className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">{t('auditTrail.columns.entity')}: </span>
              <span>{event.entityLabel ?? event.entityId ?? event.entityType}</span>
              {/* Suppressed when the label already fell back to the type, which
                  would otherwise read "plugins (plugins)". */}
              {(event.entityLabel ?? event.entityId) && (
                <span className="text-muted-foreground">
                  {' '}
                  ({event.entityType}
                  {event.entityId ? ` · ${event.entityId}` : ''})
                </span>
              )}
            </div>
            {event.storeId && (
              <div>
                <span className="text-muted-foreground">{t('auditTrail.columns.store')}: </span>
                <span className="font-mono text-xs">{event.storeId}</span>
              </div>
            )}
          </section>

          <AuditChangesDiff
            changes={event.changes}
            context={event.context}
            fidelity={event.fidelity}
            sourcePath={event.sourcePath}
          />

          {remainingContext.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-sm font-semibold">{t('auditTrail.detail.context')}</h4>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {remainingContext.map(([key, value]) => (
                  <Fragment key={key}>
                    <dt className="text-muted-foreground font-mono text-xs">{key}</dt>
                    <dd>
                      <ContextValue value={value} />
                    </dd>
                  </Fragment>
                ))}
              </dl>
            </section>
          )}
        </div>

        <div className="text-muted-foreground space-y-0.5 border-t px-6 py-3 text-xs">
          {event.requestId && (
            <div>
              {t('auditTrail.detail.requestId')}: <span className="font-mono">{event.requestId}</span>
            </div>
          )}
          {event.sourcePath && (
            <div>
              {t('auditTrail.detail.sourcePath')}: <span className="font-mono">{event.sourcePath}</span>
            </div>
          )}
          <div>
            {t('auditTrail.detail.via')}: {event.via}
          </div>
          {event.reason && (
            <div>
              {t('auditTrail.detail.reason')}: {event.reason}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
